import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { Tier, TierUpdateInput } from "@colab/shared";

export type UpdateTierInput = {
  programId: string;
  id: string;
  patch: TierUpdateInput;
};

type UpdateTierResponse = { ok: true; tier: Tier };

// PATCH /api/admin/tiers/programs/:programId/tiers/:id
//
// Pessimistic. Name / color / badge_path edits ripple to every partner card
// that carries this tier's badge, so on success we also invalidate
// `partners.all`. The badge surface in particular is hot — admin lists, the
// partner detail page, and the portal sidebar all read it.
export function useUpdateTier() {
  const queryClient = useQueryClient();
  return useMutation<UpdateTierResponse, ApiClientError, UpdateTierInput>({
    mutationFn: ({ programId, id, patch }) =>
      apiFetch<UpdateTierResponse>(
        `/admin/tiers/programs/${programId}/tiers/${id}`,
        {
          method: "PATCH",
          body: JSON.stringify(patch),
        },
      ),
    onSuccess: (_data, { programId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.program(programId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update tier");
    },
  });
}
